<template>
  <v-app class="kf-login">
    <div class="kf-login__page">
      <LoginBackdrop />

      <div class="kf-login__inner">
        <!-- The white-lettered variant of the wordmark. It already exists for
             the sidebar (Drawer.vue), which is the same navy this page uses. -->
        <img
          class="kf-login__logo"
          src="@/assets/images/KarroFin-on-dark.svg"
          alt="KarroFin"
        />

        <div class="kf-login__card">
          <v-form ref="login_form" lazy-validation @submit.prevent="onSubmit">
            <!-- ------------------------------------------ step 1: mobile -->
            <template v-if="step === 1">
              <h1 class="kf-login__title">Sign in</h1>
              <p class="kf-login__subtitle">KarroFin Admin Panel</p>

              <label class="kf-login__label" for="kf-login-mobile">
                {{ $lang.MOBILE }} number
              </label>
              <v-text-field
                id="kf-login-mobile"
                v-model="mobile"
                class="kf-field"
                outlined
                hide-details="auto"
                prefix="+91"
                placeholder="90000 00000"
                :rules="$rules.MOBILE"
                maxlength="10"
              ></v-text-field>

              <LoginMessage :error="error" :notice="notice" />

              <v-btn
                :loading="login_loading"
                class="kf-btn mt-6"
                color="secondary_2"
                type="submit"
                depressed
                block
              >
                {{ $lang.CONTINUE }}
              </v-btn>
            </template>

            <!-- --------------------------------------------- step 2: OTP -->
            <template v-else>
              <button
                type="button"
                class="kf-login__back"
                @click="goBack"
              >
                <LucideIcon name="arrow-left" :size="16" />
                <span>Change number</span>
              </button>

              <h1 class="kf-login__title">Enter OTP</h1>
              <p class="kf-login__subtitle">
                Sent to <strong>{{ mobile_display }}</strong>
              </p>

              <OtpInput
                ref="otp_input"
                :value="otp"
                class="mt-6"
                @input="onOtpInput"
                :length="OTP_LENGTH"
                :error="!!error"
                :disabled="verify_loading"
                @complete="verify"
              />
              <!-- The server echoes the OTP back in development. Kept hidden
                   rather than removed -- it is what makes the flow testable
                   against a box with no SMS gateway. -->
              <small class="d-none">{{ otp_text }}</small>

              <LoginMessage :error="error" :notice="notice" />

              <v-btn
                :loading="verify_loading"
                class="kf-btn mt-6"
                color="secondary_2"
                type="submit"
                depressed
                block
              >
                {{ $lang.LOGIN }}
              </v-btn>

              <p class="kf-login__resend">
                <span v-if="resend_seconds" class="kf-login__resend-wait">
                  Resend OTP in {{ resend_display }}
                </span>
                <button
                  v-else
                  type="button"
                  class="kf-login__resend-link"
                  @click="resendOtp"
                >
                  Resend OTP
                </button>
              </p>
            </template>
          </v-form>
        </div>

        <!-- Says how you get in, not that the page is secure: the OTP step is
             about to ask for a code, and this is where someone looks to find
             out whether that is expected. -->
        <p class="kf-login__trust">
          <LucideIcon name="badge-check" :size="15" />
          <span>Secure OTP sign-in</span>
        </p>

        <p class="kf-login__footer">&copy; KarroFin</p>
      </div>
    </div>
  </v-app>
</template>

<script type="text/javascript">
import LucideIcon from "@/components/ui/LucideIcon.vue";
// Imported eagerly, not as `() => import(...)` like the rest of the page's
// dependencies used to be: the OTP cells are focused through `$refs` on the
// tick after the step changes, and an async component has not resolved by then,
// so the ref is empty and the person lands on a row of boxes with no cursor.
import OtpInput from "@/components/karrofin/OtpInput.vue";

/**
 * The product-preview field behind the card.
 *
 * Three panels quoting the panel's own furniture -- a KPI row, a bar chart and
 * a list row -- so the front door shows what is behind it. Hand-written inline
 * rather than an asset because vue.config.js adds no webpack rules, so the
 * stock CLI routes .svg through file-loader: an import is a URL, CSS cannot
 * reach inside it, and it could not be drawn in this page's palette. Drawer.vue
 * documents the same wall from the other side. LucideIcon.vue takes the same
 * way out for the same reason.
 *
 * A local component like LoginMessage below, on that comment's own argument:
 * nothing outside this page uses it, and sixty lines of SVG at the top of the
 * template would push the form out of sight.
 *
 * Decorative only -- aria-hidden, and pointer-events:none in CSS so it can
 * never swallow a click meant for the form. The card already announces "Sign in
 * / KarroFin Admin Panel", so there is nothing here for a screen reader.
 *
 * Deliberately static. This is the one screen where people are transcribing a
 * code out of an SMS, and a drifting background is the wrong place to spend
 * their attention -- which also keeps the page clear of a prefers-reduced-motion
 * guard, a discipline this codebase does not yet have anywhere.
 */
const LoginBackdrop = {
  name: "LoginBackdrop",
  functional: true,
  render(h) {
    return h("svg", {
      class: "kf-login__art",
      attrs: {
        viewBox: "0 0 1440 900",
        preserveAspectRatio: "xMidYMid slice",
        "aria-hidden": "true",
        focusable: "false",
      },
      domProps: { innerHTML: ART },
    });
  },
};

/*
 * The geometry, as a string.
 *
 * Rounded corners are 10px on the 1440-wide viewBox, matching --kf-radius, so
 * the shapes read as this app's cards rather than as generic rectangles.
 *
 * Everything is white at low alpha over the navy, with ONE exception: a single
 * chart bar in --kf-sidebar-accent. That orange is spent sparingly across the
 * panel -- the sidebar's active marker, and this page's own corner bloom -- and
 * a second one here would turn an accent into decoration.
 *
 * It is held at 0.22, well under the white shapes' apparent weight, because the
 * Continue button is solid #fa4616 fifty pixels away. Drawn any stronger the
 * two read as a pair and the eye has to choose between them -- on a page whose
 * entire job is to get one button pressed.
 *
 * `fade` keeps the middle clear. The card sits on white with its own shadow, so
 * nothing inside it changes contrast, but the panels still have to get out of
 * the way or the page reads as clutter behind glass. An SVG mask rather than
 * CSS mask-image: self-contained, and where it is unsupported the panels stay
 * visible instead of vanishing.
 */
const ART = `
  <defs>
    <radialGradient id="kf-fade" cx="50%" cy="50%" r="62%">
      <stop offset="0%" stop-color="#000"/>
      <stop offset="45%" stop-color="#000"/>
      <stop offset="100%" stop-color="#fff"/>
    </radialGradient>
    <mask id="kf-clear">
      <rect width="1440" height="900" fill="url(#kf-fade)"/>
    </mask>
    <pattern id="kf-dots" width="26" height="26" patternUnits="userSpaceOnUse">
      <circle cx="1.5" cy="1.5" r="1.5" fill="#ffffff" fill-opacity="0.05"/>
    </pattern>
  </defs>

  <rect width="1440" height="900" fill="url(#kf-dots)" mask="url(#kf-clear)"/>

  <g mask="url(#kf-clear)" class="kf-login__art-panels">
    <!-- KPI row, upper left. KpiCard.vue: icon square, value bar, label bar -->
    <g transform="translate(96 132)">
      <rect width="470" height="128" rx="10" fill="#ffffff" fill-opacity="0.07"/>
      <g fill="#ffffff">
        <rect x="26" y="26" width="26" height="26" rx="7" fill-opacity="0.14"/>
        <rect x="26" y="68" width="84" height="14" rx="7" fill-opacity="0.13"/>
        <rect x="26" y="92" width="52" height="8" rx="4" fill-opacity="0.08"/>

        <rect x="182" y="26" width="26" height="26" rx="7" fill-opacity="0.14"/>
        <rect x="182" y="68" width="70" height="14" rx="7" fill-opacity="0.13"/>
        <rect x="182" y="92" width="44" height="8" rx="4" fill-opacity="0.08"/>

        <rect x="338" y="26" width="26" height="26" rx="7" fill-opacity="0.14"/>
        <rect x="338" y="68" width="92" height="14" rx="7" fill-opacity="0.13"/>
        <rect x="338" y="92" width="58" height="8" rx="4" fill-opacity="0.08"/>
      </g>
    </g>

    <!-- Bar chart, lower right. The seventh bar is the accent. -->
    <g transform="translate(902 560)">
      <rect width="442" height="212" rx="10" fill="#ffffff" fill-opacity="0.07"/>
      <rect x="28" y="26" width="96" height="10" rx="5" fill="#ffffff" fill-opacity="0.12"/>
      <g fill="#ffffff" fill-opacity="0.13">
        <rect x="28"  y="118" width="34" height="62" rx="6"/>
        <rect x="86"  y="96"  width="34" height="84" rx="6"/>
        <rect x="144" y="132" width="34" height="48" rx="6"/>
        <rect x="202" y="80"  width="34" height="100" rx="6"/>
        <rect x="260" y="104" width="34" height="76" rx="6"/>
        <rect x="318" y="70"  width="34" height="110" rx="6"/>
      </g>
      <rect x="376" y="58" width="34" height="122" rx="6" fill="#fa4616" fill-opacity="0.22"/>
    </g>

    <!-- One table row, mid right: avatar, two lines, a status pill -->
    <g transform="translate(978 300)">
      <rect width="366" height="88" rx="10" fill="#ffffff" fill-opacity="0.06"/>
      <circle cx="52" cy="44" r="20" fill="#ffffff" fill-opacity="0.12"/>
      <g fill="#ffffff">
        <rect x="90" y="32" width="120" height="11" rx="5.5" fill-opacity="0.12"/>
        <rect x="90" y="52" width="76"  height="8"  rx="4"   fill-opacity="0.07"/>
      </g>
      <rect x="264" y="32" width="74" height="24" rx="12" fill="#ffffff" fill-opacity="0.09"/>
    </g>
  </g>
`;

/**
 * The inline message strip. A local functional component rather than a file of
 * its own: it is four lines of markup and nothing outside this page uses it.
 * It replaces the corner snackbar, which put the reason a login failed in the
 * opposite corner from the field that caused it.
 */
const LoginMessage = {
  name: "LoginMessage",
  functional: true,
  props: {
    error: { type: String, default: "" },
    notice: { type: String, default: "" },
  },
  render(h, ctx) {
    const { error, notice } = ctx.props;
    if (!error && !notice) return null;
    const bad = !!error;
    return h(
      "div",
      {
        class: ["kf-msg", bad ? "kf-msg--error" : "kf-msg--ok"],
        attrs: { role: "alert" },
      },
      [
        h(LucideIcon, {
          props: { name: bad ? "circle-alert" : "circle-check", size: 16 },
          class: "kf-msg__icon",
        }),
        h("span", error || notice),
      ]
    );
  },
};

/** Digits in the code. `rules.js` and the server agree on five. */
const OTP_LENGTH = 5;
/** Seconds before "Resend OTP" becomes live again. */
const RESEND_WAIT = 30;

export default {
  components: {
    LoginBackdrop,
    LoginMessage,
    OtpInput,
  },
  data() {
    return {
      OTP_LENGTH,
      step: 1,
      mobile: "",
      otp: "",
      otp_text: "",

      otp_token: "",
      login_loading: false,
      verify_loading: false,

      // The inline strip. Exactly one of the two is ever set.
      error: "",
      notice: "",

      resend_seconds: 0,
      resend_timer: null,
    };
  },
  computed: {
    mobile_display() {
      const m = String(this.mobile || "");
      return m.length === 10 ? `+91 ${m.slice(0, 5)} ${m.slice(5)}` : `+91 ${m}`;
    },
    resend_display() {
      const s = this.resend_seconds;
      return `0:${String(s).padStart(2, "0")}`;
    },
  },
  watch: {
    // Typing is the person's answer to the message, so the message goes.
    // The OTP is handled by `onOtpInput` rather than a watcher here, because
    // the code is also cleared programmatically -- and that must not wipe the
    // error explaining why it was cleared.
    mobile() {
      this.clearMessages();
    },
  },
  mounted() {
    // token for staging
    if (process.env.NODE_ENV === "staging") {
      // sessionStorage.setItem("token","f6719ffa789492dcf8eaea0bda8b6f8cdb51b7f7") // Production
      sessionStorage.setItem(
        "token",
        "e43d2cee94db7485ef72389dc7c852f7e026462b"
      ); // Staging
      sessionStorage.setItem(
        "permission_list",
        '["LAS" ,"Business Loan","Refresh Request", "Notification Panel", "Business Loan details","Customer","Manual Payment","Pre Verification","Loan Applied","Team","EMI Analysis","Payment","Review","Manage your Team","Customer details","Export Customer CSV","Service Area","Manage User Contacts","Manage FAQ","Session Detail","Session History","New Customer","View All Documents", "PL Funnel","Credit Card","Reports"]'
      );
    }
  },
  beforeDestroy() {
    this.stopResendTimer();
  },
  methods: {
    /**
     * Every failure in the app is reported by calling `showSnakeBar` on the
     * component that made the request -- both the business failures raised by
     * `request_POST` (main.js) and the transport ones raised by handle401 /
     * handle403 / handle500. A component method shadows the global mixin, so
     * defining it here diverts all of them into the card without touching
     * main.js or affecting any other screen.
     */
    showSnakeBar(type, text) {
      if (type === "success") {
        this.notice = text;
        this.error = "";
      } else {
        this.error = text || "Something went wrong. Please try again.";
        this.notice = "";
      }
    },
    /* A keystroke in the OTP cells -- as opposed to the code being reset. */
    onOtpInput(value) {
      this.otp = value;
      this.clearMessages();
    },
    clearMessages() {
      this.error = "";
      this.notice = "";
    },

    /* Enter, and both buttons. The form is the same on both steps, so the
       submit handler is what decides which request it means. */
    onSubmit() {
      if (this.step === 1) this.login();
      else this.verify();
    },

    startResendTimer() {
      this.stopResendTimer();
      this.resend_seconds = RESEND_WAIT;
      this.resend_timer = setInterval(() => {
        this.resend_seconds -= 1;
        if (this.resend_seconds <= 0) this.stopResendTimer();
      }, 1000);
    },
    stopResendTimer() {
      if (this.resend_timer) clearInterval(this.resend_timer);
      this.resend_timer = null;
      this.resend_seconds = 0;
    },

    /* Back to the number, from the OTP step. */
    goBack() {
      this.step = 1;
      this.otp = "";
      this.otp_token = "";
      this.otp_text = "";
      this.stopResendTimer();
      this.clearMessages();
    },

    /* request for login */
    login() {
      const self = this;
      if (this.$refs.login_form.validate() == false) return false;
      self.clearMessages();
      self.login_loading = true;
      var form = new FormData();
      form.append("mobile", self.mobile);
      form.append("account_type", self.$keys.ADMIN);

      const successHandler = (response) => {
        if (response.data.success) {
          self.otp_token = response.headers["otp-token"];
          // self.otp = response.data["otp"]
          self.otp_text = response.data["otp"];
          self.step = 2;
          self.startResendTimer();
          // The cells are useless until one of them has the cursor, and the
          // component only just mounted.
          self.$nextTick(() => {
            if (self.$refs.otp_input) self.$refs.otp_input.focus(0);
          });
        }
      };
      const finallyHandler = () => {
        self.login_loading = false;
      };
      this.request_POST(
        self,
        this.$urls.LOGIN,
        form,
        successHandler,
        null,
        null,
        finallyHandler
      );
    },
    /* submit otp and verify data */
    verify() {
      // `complete` from the OTP cells submits on the last digit; Enter can
      // arrive on top of it.
      if (this.verify_loading) return false;
      if (this.otp.length != OTP_LENGTH) {
        this.error = `Enter the ${OTP_LENGTH}-digit OTP`;
        return false;
      }
      const self = this;
      self.clearMessages();
      self.verify_loading = true;
      var form = new URLSearchParams();
      form.append("otp", this.otp);

      var header = {
        "otp-token": self.otp_token,
      };
      const successHandler = (response) => {
        if (response.data.success) {
          sessionStorage.setItem("token", response.headers.token);
          this.$store.dispatch(
            "setPermissionList",
            response.data.permission_list
          );
          sessionStorage.setItem(
            "permission_list",
            JSON.stringify(response.data.permission_list)
          );
          // Filtered view: which filters this member may not set, per screen.
          // Names only -- the server never sends the values, so the restriction
          // cannot be read out of here. Stored the same way as the permission
          // list, which means it is refreshed at login and only at login.
          this.$store.dispatch(
            "setViewFilterLocks",
            response.data.view_filter_locks || {}
          );
          sessionStorage.setItem(
            "view_filter_locks",
            JSON.stringify(response.data.view_filter_locks || {})
          );
          // Values, for the four demo-data screens only. Those screens build
          // their rows in the browser, so filtering them at all means the
          // browser has to know what to filter by -- there is no way to both
          // apply it here and hide it. The six live screens keep their values
          // on the server, where the queryset is restricted.
          sessionStorage.setItem(
            "view_filter_demo_values",
            JSON.stringify(response.data.view_filter_demo_values || {})
          );

          // Landing screen is the KarroFin dashboard -- "Personal Loan"
          // (pre-verification) is no longer part of the sidebar.
          self.$router.push({ name: "karrofin-dashboard" });
        } else {
          // A rejected code leaves the cells full, which reads as "nothing
          // happened". Clear them; the cursor goes back below, once the cells
          // are no longer disabled.
          self.otp = "";
        }
      };
      const finallyHandler = () => {
        self.verify_loading = false;
        // Not in the success handler: the cells are still disabled at that
        // point, and a disabled input cannot take focus.
        if (self.otp === "") {
          self.$nextTick(() => {
            if (self.$refs.otp_input) self.$refs.otp_input.focus(0);
          });
        }
      };
      self.request_POST(
        self,
        self.$urls.VERIFY_OTP,
        form,
        successHandler,
        header,
        null,
        finallyHandler
      );
    },
    /* resend OTP */
    resendOtp() {
      const self = this;
      if (self.resend_seconds) return false;
      self.otp = "";
      self.clearMessages();
      self.startResendTimer();
      var form = new URLSearchParams();
      var header = {
        "otp-token": self.otp_token,
      };
      const successHandler = (response) => {
        if (response.data.success) {
          self.otp_text = response.data["otp"] || self.otp_text;
          self.showSnakeBar("success", "OTP resent");
        }
      };
      self.request_POST(
        self,
        self.$urls.RESEND_OTP,
        form,
        successHandler,
        header
      );
    },
  },
};
</script>

<style scoped>
/* ------------------------------------------------------------- the page --- */
/* kf-tokens.css paints every `v-app` with the grey canvas, deliberately, because
   this page has no Base.vue wrapper to set one. This screen is the exception:
   it is the product's front door, so it gets the product's navy. The extra
   class is what lets it out-specify that rule. */
.kf-login.v-application.theme--light {
  background: var(--kf-sidebar) !important;
}
.kf-login >>> .v-application--wrap {
  min-height: 100vh;
}

.kf-login__page {
  flex: 1 1 auto;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  /* The navy of the sidebar, running into the brand blue, closing on a deeper
     shade so the bottom of the screen has some weight. The warm bloom in the
     top-left is the app's own accent orange at a tenth strength -- enough to
     stop the page reading as one flat rectangle, not enough to be a colour. */
  background: radial-gradient(
      ellipse at 18% 8%,
      rgba(250, 70, 22, 0.1),
      transparent 55%
    ),
    linear-gradient(
      160deg,
      var(--kf-sidebar) 0%,
      var(--kf-brand) 55%,
      #0f2740 100%
    );
}
/* The product-preview field. Absolute so it fills the gradient without
   affecting layout; pointer-events off so it can never take a click meant for
   the form; z-index 0 against the inner block's 1, because both are positioned
   and source order alone would leave the card underneath. */
.kf-login__art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}
.kf-login__page {
  position: relative;
}
.kf-login__inner {
  position: relative;
  z-index: 1;
  width: 100%;
  /* 440 rather than 420: the card has to hold its own against a busier field
     without stepping up into a different size class. */
  max-width: 440px;
}
.kf-login__logo {
  display: block;
  width: 190px;
  margin: 0 auto 28px;
}

/* ------------------------------------------------------------- the card --- */
.kf-login__card {
  background: var(--kf-surface);
  border-radius: 14px;
  padding: 40px 36px;
  /* --kf-shadow-hover is tuned for the grey canvas and disappears on navy. */
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28), 0 2px 6px rgba(0, 0, 0, 0.12);
  /* A defined edge where the backdrop's panels pass behind the card. Shadow
     alone reads as a soft blur against a field that is no longer flat. */
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.kf-login__title {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--kf-text);
  margin: 0;
}
.kf-login__subtitle {
  font-size: var(--kf-fs-hint);
  color: var(--kf-text-muted);
  margin: 4px 0 0;
}
.kf-login__subtitle strong {
  color: var(--kf-text-body);
  font-weight: 700;
  white-space: nowrap;
}
.kf-login__label {
  display: block;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: var(--kf-text-muted);
  margin: 24px 0 6px;
}
/* Icon and words on one line, centred under the card. --kf-fs-label is the
   floor kf-tokens.css sets for type; this sits on it rather than under it.
   0.72 white clears AA at every stop of the gradient behind it: 7.6:1 on the
   navy, 5.4:1 at --kf-brand where it is lightest. */
.kf-login__trust {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.72);
  margin: 18px 0 0;
}
/* Was 0.45, which is 4.0:1 against the navy -- under the 4.5:1 bar
   kf-tokens.css holds every one of its own text tokens to.
   Measured at the LIGHTEST stop of the gradient behind it, --kf-brand #194e76,
   not the navy: this text sits below the card, around where the gradient peaks,
   and 0.62 is 4.48:1 there -- a hair short. 0.68 is 5.0:1 at that stop and
   7.0:1 on the navy. */
.kf-login__footer {
  text-align: center;
  font-size: var(--kf-fs-label);
  color: rgba(255, 255, 255, 0.68);
  margin: 10px 0 0;
}

/* --------------------------------------------------------------- fields --- */
.kf-field >>> .v-input__slot {
  min-height: 48px !important;
  background: var(--kf-surface-2) !important;
  border-radius: var(--kf-radius) !important;
  transition: box-shadow 0.15s ease;
}
.kf-field >>> fieldset {
  border-color: var(--kf-border);
  border-radius: var(--kf-radius);
}
.kf-field.v-input--is-focused >>> fieldset {
  border-width: 1px;
  border-color: var(--kf-brand);
}
.kf-field.v-input--is-focused >>> .v-input__slot {
  box-shadow: 0 0 0 3px rgba(25, 78, 118, 0.12);
}
.kf-field >>> input {
  font-size: 15px;
  font-weight: 600;
  color: var(--kf-text);
}
/* The country code is a fact about the field, not something you typed -- so it
   is quieter than the number and separated by a rule rather than a space. */
.kf-field >>> .v-text-field__prefix {
  color: var(--kf-text-muted);
  font-weight: 600;
  padding-right: 10px;
  margin-right: 10px;
  border-right: 1px solid var(--kf-border);
}
.kf-field >>> .v-messages__message {
  font-size: var(--kf-fs-label);
  line-height: 1.4;
  padding-top: 4px;
}

/* -------------------------------------------------------------- buttons --- */
.kf-btn.v-btn {
  height: 48px !important;
  border-radius: var(--kf-radius);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #ffffff;
}

/* Both of the page's plain buttons -- "Change number" and "Resend OTP". They
   are real buttons rather than clickable <p>s (the pattern this page used to
   use) so they are reachable by keyboard, which means the browser's default
   chrome has to come off. */
.kf-login__back,
.kf-login__resend-link {
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font: inherit;
}
.kf-login__back:focus-visible,
.kf-login__resend-link:focus-visible {
  outline: 2px solid var(--kf-brand);
  outline-offset: 2px;
  border-radius: 4px;
}

.kf-login__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: var(--kf-brand);
  margin-bottom: 14px;
}
.kf-login__back:hover {
  color: var(--kf-brand-strong);
  text-decoration: underline;
}

.kf-login__resend {
  text-align: center;
  font-size: var(--kf-fs-label);
  margin: 16px 0 0;
}
.kf-login__resend-wait {
  color: var(--kf-text-muted);
}
.kf-login__resend-link {
  color: var(--kf-brand);
  font-weight: 700;
}
.kf-login__resend-link:hover {
  color: var(--kf-brand-strong);
  text-decoration: underline;
}

/* ------------------------------------------------------ message strip --- */
.kf-login__card >>> .kf-msg {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid;
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  line-height: 1.45;
}
.kf-login__card >>> .kf-msg__icon {
  margin-top: 1px;
}
.kf-login__card >>> .kf-msg--error {
  color: var(--kf-danger);
  background: var(--kf-danger-soft);
  border-color: rgba(192, 57, 43, 0.28);
}
.kf-login__card >>> .kf-msg--ok {
  color: var(--kf-success);
  background: var(--kf-success-soft);
  border-color: rgba(30, 122, 77, 0.28);
}

/* -------------------------------------------------- narrower than a laptop --- */
/* The art is a fixed 1440x900 scaled with `slice`, so a narrower viewport does
   not reflow it -- it CROPS it, from the centre out. Below about 960px that
   cuts each panel through the middle and they read as clipped rectangles
   rather than as cards passing behind the form. The dot grid and the gradient
   tile at any size, so those stay and the panels go.
   960px is the panel's own mid breakpoint (FilterBar.vue, kf-tokens.css). */
@media (max-width: 960px) {
  .kf-login__art >>> .kf-login__art-panels {
    display: none;
  }
}

/* ------------------------------------------------------------ on a phone --- */
@media (max-width: 600px) {
  .kf-login__page {
    padding: 24px 16px;
  }
  .kf-login__logo {
    width: 150px;
    margin-bottom: 22px;
  }
  .kf-login__card {
    padding: 28px 20px;
  }
}
</style>
